import React, { useMemo, useState } from 'react'

import useNameFormatter from '@divvy-web/hooks.usenameformatter'
import { FormattedMessage } from '@divvy-web/i18n'
import ExpansionPanel from '@divvy-web/skylab.expansionpanel'
import FileViewer from '@divvy-web/skylab.fileviewer'
import Icon from '@divvy-web/skylab.icon'
import TextLink from '@divvy-web/skylab.textlink'

import { useDocumentTypes } from '@sputnik/pages/DashboardPages/CreditApplicationsDataGrid/DashboardDetailsDrawer/DocumentsTabContent/useDocumentTypes'
import { GetCreditApplicationDetails_creditApplication_CreditApplication_requiredDocs_CreditApplicationDocument } from '@sputnik/pages/DashboardPages/CreditApplicationsDataGrid/DashboardDetailsDrawer/GetCreditApplicationDetails.gql'

import styles from './styles.module.scss'

type CreditApplicationDocument =
  GetCreditApplicationDetails_creditApplication_CreditApplication_requiredDocs_CreditApplicationDocument

export interface DocumentUploadItemProps {
  salesforceCreditId: string
  document: CreditApplicationDocument
}

const DocumentUploadItem = ({
  salesforceCreditId,
  document: { documentType, status, fileName, documentUuid, signedUrl },
}: DocumentUploadItemProps) => {
  const [getClassName, makeTestId] = useNameFormatter('DocumentUploadItem')
  const [isShowingFile, setIsShowingFile] = useState(false)

  const { options } = useDocumentTypes({ salesforceCreditId })

  const { label: title, header, footer, content } = options?.find(({ value }) => value === documentType) ?? {}

  // statuses are derived from
  // https://git.hq.bill.com/backend/compliance/-/blob/master/apps/shared_db/lib/shared_db/cip_documents/cip_document_activity.ex#L13
  const { statusText, statusIcon } = useMemo(() => {
    switch (status) {
      case 'reviewed':
        return {
          statusText: (
            <FormattedMessage
              defaultMessage='Accepted'
              id='sputnik.RequiredDocument__aFyFm0'
            />
          ),
          statusIcon: (
            <Icon
              className={styles[getClassName('heading-title-approved')]}
              name='feedback-success'
            />
          ),
        }
      case 'rejected':
        return {
          statusText: (
            <FormattedMessage
              defaultMessage='Rejected'
              id='sputnik.RequiredDocument__lYvasN'
            />
          ),
          statusIcon: (
            <Icon
              className={styles[getClassName('heading-title-denied')]}
              name='feedback-error'
            />
          ),
        }
      case 'uploaded':
      case 'comment':
        return {
          statusText: (
            <FormattedMessage
              defaultMessage='In review'
              id='sputnik.RequiredDocument__plemGH'
            />
          ),
          statusIcon: (
            <Icon
              className={styles[getClassName('heading-title-in-review')]}
              name='feedback-in-progress'
            />
          ),
        }
      case 'requested':
      case 'deleted':
      default:
        return {
          statusText: (
            <FormattedMessage
              defaultMessage='Not uploaded'
              id='sputnik.RequiredDocument__W93C+Q'
            />
          ),
          statusIcon: <Icon name='feedback-warning' />,
        }
    }
  }, [getClassName, status])

  return (
    <ExpansionPanel
      heading={
        <div className={styles[getClassName('heading')]}>
          {statusIcon}
          <div className={styles[getClassName('heading-title')]}>
            <div className={styles[getClassName('heading-title-document')]}>{title}</div>
            <div className={styles[getClassName('heading-title-status')]}>{statusText}</div>
          </div>
        </div>
      }
    >
      <div className={styles[getClassName('description')]}>
        <div className={styles[getClassName('description-header')]}>{header}</div>
        <div className={styles[getClassName('description-content')]}>
          {Array.isArray(content) ? (
            <ul>
              {content.map((c) => (
                <li key={c}>{c}</li>
              ))}
            </ul>
          ) : (
            content
          )}
        </div>
        <div className={styles[getClassName('description-footer')]}>{footer}</div>
      </div>
      {fileName && signedUrl && (
        <div className={styles[getClassName('file')]}>
          <TextLink onClick={() => setIsShowingFile(true)}>{fileName}</TextLink>
          <FileViewer
            closeOnOutsideClick
            dataTestId={makeTestId('fileviewer')}
            // passing in blank for the file type seems to work just fine
            files={[{ filename: fileName, id: documentUuid, url: signedUrl, type: '' }]}
            isShowing={isShowingFile}
            reduceWidthBy={'var(--sky-drawer-width-s)'}
            onClose={() => setIsShowingFile(false)}
          />
        </div>
      )}
    </ExpansionPanel>
  )
}

export default DocumentUploadItem
